import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { axiosBaseQuery } from '../base.service';
import type {
  GetWebWidgetDetailArgs,
  GetWebWidgetsByChannelArgs,
  RenewWebWidgetSecurityTokenArgs,
  UpdateWebWidgetPublishSettingsArgs,
  WebWidgetDetailResponseDto,
  WebWidgetSummaryDto,
} from './web-widgets.api.types';
import { fromApiWebWidgetDetail, toApiWebWidgetPublishSettings } from './web-widgets.mapper';
import type { WebWidgetDetail } from './types';

export const WebWidgetsService = createApi({
  reducerPath: 'WebWidgetsService',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getWebWidgetsByChannel: builder.query<WebWidgetSummaryDto[], GetWebWidgetsByChannelArgs>({
      query: ({ webChannelId }) => ({
        url: '/v1/web_widgets',
        method: HttpMethod.GET,
        params: {
          web_channel_id: webChannelId,
        },
      }),
    }),
    getWebWidgetDetail: builder.query<WebWidgetDetail, GetWebWidgetDetailArgs>({
      query: ({ widgetId }) => ({
        url: `/v1/web_widgets/${widgetId}`,
        method: HttpMethod.GET,
      }),
      transformResponse: (response: WebWidgetDetailResponseDto) => fromApiWebWidgetDetail(response),
    }),
    // Writes only the given fields; the rest of the widget record stays as saved.
    updateWebWidgetPublishSettings: builder.mutation<void, UpdateWebWidgetPublishSettingsArgs>({
      query: ({ settings, widgetId }) => ({
        url: `/v1/web_widgets/${widgetId}/update`,
        method: HttpMethod.POST,
        data: toApiWebWidgetPublishSettings(settings),
        extraOptions: { notification: { autoHandleSuccess: false } },
      }),
    }),
    // Resolves to the new security token.
    renewWebWidgetSecurityToken: builder.mutation<string | null, RenewWebWidgetSecurityTokenArgs>({
      query: ({ widgetId }) => ({
        url: `/v1/web_widgets/${widgetId}/security_token/update`,
        method: HttpMethod.POST,
        extraOptions: { notification: { autoHandleSuccess: false } },
      }),
      transformResponse: (response: WebWidgetDetailResponseDto) =>
        fromApiWebWidgetDetail(response).publishing.securityToken,
    }),
  }),
});

export const {
  useGetWebWidgetDetailQuery,
  useGetWebWidgetsByChannelQuery,
  useRenewWebWidgetSecurityTokenMutation,
  useUpdateWebWidgetPublishSettingsMutation,
} = WebWidgetsService;
